import React, { useRef, useState, useEffect, useCallback } from "react";
import {
  motion,
  useScroll,
  useTransform,
  useReducedMotion,
} from "framer-motion";
import { greeting, socialMediaLinks } from "../portfolio";
import { FaGithub, FaLinkedinIn, FaInstagram } from "react-icons/fa";
import { HiMail } from "react-icons/hi";
import LightningBackground from "./LightningBackground";
import ScrambleText from "./ScrambleText";
import {
  unlockAudio,
  initAudio,
  playHover,
  playClick,
} from "../utils/soundEngine";

function AnimatedChars({ text, delay = 0, className }) {
  const shouldReduce = useReducedMotion();
  return (
    <span className={className} aria-label={text}>
      {text.split("").map((char, i) => (
        <motion.span
          key={i}
          initial={shouldReduce ? { opacity: 0 } : { y: "110%", opacity: 0 }}
          animate={shouldReduce ? { opacity: 1 } : { y: 0, opacity: 1 }}
          transition={{
            duration: shouldReduce ? 0.01 : 0.35,
            delay: shouldReduce ? 0 : delay + i * 0.03,
            ease: [0.16, 1, 0.3, 1],
          }}
          className="inline-block"
          aria-hidden="true"
        >
          {char}
        </motion.span>
      ))}
    </span>
  );
}

function CountUp({ end, delay = 0, duration = 1500 }) {
  const [count, setCount] = useState(0);

  useEffect(() => {
    let startTime;
    let frameId;
    const timerId = setTimeout(() => {
      const animate = (now) => {
        if (!startTime) startTime = now;
        const progress = Math.min((now - startTime) / duration, 1);
        const eased = 1 - Math.pow(1 - progress, 3);
        setCount(Math.round(end * eased));
        if (progress < 1) frameId = requestAnimationFrame(animate);
      };
      frameId = requestAnimationFrame(animate);
    }, delay);
    return () => {
      clearTimeout(timerId);
      if (frameId) cancelAnimationFrame(frameId);
    };
  }, [end, delay, duration]);

  return count;
}

function MagneticWrap({ children, className, strength = 0.3 }) {
  const ref = useRef(null);
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const shouldReduce = useReducedMotion();

  const handleMove = useCallback(
    (e) => {
      if (shouldReduce || !ref.current) return;
      const { left, top, width, height } = ref.current.getBoundingClientRect();
      setPos({
        x: (e.clientX - left - width / 2) * strength,
        y: (e.clientY - top - height / 2) * strength,
      });
    },
    [strength, shouldReduce]
  );

  const handleLeave = useCallback(() => setPos({ x: 0, y: 0 }), []);

  return (
    <motion.div
      ref={ref}
      onMouseMove={handleMove}
      onMouseLeave={handleLeave}
      animate={{ x: pos.x, y: pos.y }}
      transition={{ type: "spring", stiffness: 300, damping: 28, mass: 0.1 }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

function Hero({ darkMode = true, isLoaded = false }) {
  const firstName = greeting.full_name.split(" ")[0];
  const lastName = greeting.full_name.split(" ")[1];
  const sectionRef = useRef(null);
  const nameRef = useRef(null);
  const shouldReduce = useReducedMotion();

  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start start", "end start"],
  });

  // Multi-Plane 3D Parallax Scroll Layers (Guarded by reduced motion)
  const rawOpacity = useTransform(scrollYProgress, [0, 0.45], [1, 0]);
  const rawScale = useTransform(scrollYProgress, [0, 0.5], [1, 0.97]);
  const rawYLabel = useTransform(scrollYProgress, [0, 0.5], [0, -14]);
  const rawYName = useTransform(scrollYProgress, [0, 0.5], [0, -36]);
  const rawYText = useTransform(scrollYProgress, [0, 0.5], [0, -24]);
  const rawYActions = useTransform(scrollYProgress, [0, 0.5], [0, -14]);

  const opacity = shouldReduce ? 1 : rawOpacity;
  const scale = shouldReduce ? 1 : rawScale;
  const yLabel = shouldReduce ? 0 : rawYLabel;
  const yName = shouldReduce ? 0 : rawYName;
  const yText = shouldReduce ? 0 : rawYText;
  const yActions = shouldReduce ? 0 : rawYActions;

  // Programmatically trigger click on name on initial page load & reveal to unlock audio
  useEffect(() => {
    const triggerNameClick = () => {
      if (nameRef.current) {
        try {
          nameRef.current.click();
          nameRef.current.dispatchEvent(
            new MouseEvent("click", {
              bubbles: true,
              cancelable: true,
              view: window,
            })
          );
        } catch (e) {}
      }
      unlockAudio();
      initAudio();
    };

    triggerNameClick();
    const timer = setTimeout(triggerNameClick, 300);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (isLoaded && nameRef.current) {
      try {
        nameRef.current.click();
        nameRef.current.dispatchEvent(
          new MouseEvent("click", {
            bubbles: true,
            cancelable: true,
            view: window,
          })
        );
      } catch (e) {}
      unlockAudio();
    }
  }, [isLoaded]);

  return (
    <section
      id="hero"
      ref={sectionRef}
      className="relative min-h-screen flex items-center overflow-hidden"
    >
      {/* Cinematic Procedural Lightning Strike Background */}
      <LightningBackground darkMode={darkMode} isLoaded={isLoaded} />

      <motion.div
        style={{ opacity, scale }}
        className="container-main relative z-10 pt-32 pb-24"
      >
        <div>
          {/* Plane 1: Role Label */}
          <motion.span
            style={{ y: yLabel }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="font-mono text-xs tracking-[0.25em] uppercase text-fg-faint mb-10 block cursor-default"
          >
            <ScrambleText text="Software Engineer" hover={true} />
          </motion.span>

          {/* Plane 2: Name — character reveal with deeper parallax */}
          <motion.div
            style={{ y: yName }}
            ref={nameRef}
            onClick={() => {
              unlockAudio();
              initAudio();
            }}
            onPointerDown={() => {
              unlockAudio();
            }}
            className="mb-8 cursor-default"
          >
            <h1 className="font-display text-hero font-extrabold leading-none uppercase">
              <span className="block overflow-hidden text-fg whitespace-nowrap">
                <AnimatedChars text={firstName} delay={0.25} />
              </span>
              <span className="block overflow-hidden text-fg-muted whitespace-nowrap">
                <AnimatedChars text={lastName} delay={0.45} />
                <motion.span
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.45 + lastName.length * 0.04 + 0.1 }}
                  className="inline-block text-fg"
                >
                  .
                </motion.span>
              </span>
            </h1>
          </motion.div>

          {/* Plane 3: Subtitle & Experience Ticker */}
          <motion.div style={{ y: yText }}>
            <motion.p
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.8 }}
              className="text-lg text-fg-muted max-w-lg leading-relaxed mb-4"
            >
              {greeting.subTitle}
            </motion.p>

            <motion.p
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.9 }}
              className="text-xs font-mono text-fg-faint tracking-widest uppercase mb-12"
            >
              <CountUp end={4} delay={950} duration={1200} />+ years building
              and scaling production software
            </motion.p>
          </motion.div>

          {/* Plane 4: CTAs & Actions */}
          <motion.div
            style={{ y: yActions }}
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 1.0 }}
            className="flex flex-wrap items-center gap-4 mb-14"
          >
            <MagneticWrap className="inline-block">
              <a
                href="#contact"
                onMouseEnter={() => playHover()}
                onClick={(e) => {
                  playClick();
                  e.preventDefault();
                  document
                    .getElementById("contact")
                    ?.scrollIntoView({ behavior: "smooth" });
                }}
                className="group inline-flex items-center gap-2 px-6 py-3 bg-fg text-bg font-medium text-sm rounded-full hover:bg-fg/80 transition-colors duration-300"
              >
                Get in Touch
                <span className="transition-transform duration-300 group-hover:translate-x-0.5">
                  &rarr;
                </span>
              </a>
            </MagneticWrap>
            <MagneticWrap className="inline-block">
              <a
                href={greeting.resumeLink}
                target="_blank"
                rel="noopener noreferrer"
                onMouseEnter={() => playHover()}
                onClick={() => playClick()}
                className="inline-flex items-center px-6 py-3 border border-fg-ghost text-fg-muted font-medium text-sm rounded-full hover:border-fg-faint hover:text-fg transition-all duration-300"
              >
                Resume
              </a>
            </MagneticWrap>
          </motion.div>

          {/* Socials */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 1.2 }}
            className="flex items-center gap-6"
          >
            {[
              {
                href: socialMediaLinks.github,
                icon: FaGithub,
                label: "GitHub",
              },
              {
                href: socialMediaLinks.linkedin,
                icon: FaLinkedinIn,
                label: "LinkedIn",
              },
              {
                href: `mailto:${socialMediaLinks.gmail}`,
                icon: HiMail,
                label: "Email",
              },
              {
                href: socialMediaLinks.instagram,
                icon: FaInstagram,
                label: "Instagram",
              },
            ].map(({ href, icon: Icon, label }) => (
              <MagneticWrap key={label} strength={0.4}>
                <a
                  href={href}
                  target={label === "Email" ? undefined : "_blank"}
                  rel="noopener noreferrer"
                  aria-label={label}
                  onMouseEnter={() => playHover()}
                  onClick={() => playClick()}
                  className="text-fg-faint hover:text-fg transition-colors duration-300 block p-1"
                >
                  <Icon size={18} />
                </a>
              </MagneticWrap>
            ))}
          </motion.div>
        </div>
      </motion.div>
    </section>
  );
}

export default Hero;
